function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

export type MunicipalityResourceLink = {
  id: number;
  municipality_id: number;
  title: string;
  url: string;
  cta_label: string;
  description: string | null;
  resource_type: string | null;
  keywords: string[];
  priority: number;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type MunicipalityResourceLinkInput = {
  title: string;
  url: string;
  cta_label: string;
  /** Use null to clear optional DB fields on update (JSON omits undefined). */
  description: string | null;
  resource_type: string | null;
  keywords: string[];
  priority: number;
  active: boolean;
};

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  const data = (await response.json().catch(() => ({}))) as { errors?: string[]; error?: string };
  const msg =
    Array.isArray(data.errors) && data.errors.length > 0
      ? data.errors.join("; ")
      : data.error || `Request failed (${response.status})`;
  throw new Error(msg);
}

function basePath(municipalityId: number) {
  return `/api/v1/municipalities/${municipalityId}/municipality_resource_links`;
}

export async function listMunicipalityResourceLinks(municipalityId: number): Promise<MunicipalityResourceLink[]> {
  const response = await fetch(basePath(municipalityId), {
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  return parseResponse<MunicipalityResourceLink[]>(response);
}

export async function createMunicipalityResourceLink(
  municipalityId: number,
  payload: MunicipalityResourceLinkInput
): Promise<MunicipalityResourceLink> {
  const response = await fetch(basePath(municipalityId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "include",
    body: JSON.stringify({ municipality_resource_link: payload }),
  });
  return parseResponse<MunicipalityResourceLink>(response);
}

export async function updateMunicipalityResourceLink(
  municipalityId: number,
  linkId: number,
  payload: MunicipalityResourceLinkInput
): Promise<MunicipalityResourceLink> {
  const response = await fetch(`${basePath(municipalityId)}/${linkId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "include",
    body: JSON.stringify({ municipality_resource_link: payload }),
  });
  return parseResponse<MunicipalityResourceLink>(response);
}

export type GenerateDescriptionInput = {
  title: string;
  resource_type?: string | null;
  url?: string | null;
  keywords?: string[];
};

/** ENG-1390: LLM-draft a scenario-rich description from the form's current fields (stateless). */
export async function generateMunicipalityResourceLinkDescription(
  municipalityId: number,
  input: GenerateDescriptionInput
): Promise<{ description: string }> {
  const response = await fetch(`${basePath(municipalityId)}/generate_description`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "include",
    body: JSON.stringify(input),
  });
  return parseResponse<{ description: string }>(response);
}

export async function deleteMunicipalityResourceLink(municipalityId: number, linkId: number): Promise<void> {
  const response = await fetch(`${basePath(municipalityId)}/${linkId}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "include",
  });
  await parseResponse<void>(response);
}
